import { AxiosResponse } from 'axios';

import { ApiVersion } from '@/config';
import {
  IEquipment,
  ILineLayer,
  IPointLayer,
  IPolygonLayer,
  IUnit,
  IUnitFilter,
  IUnitFilterForm,
  IUnitForm,
  IUnitShift,
  IUnitShiftAssignment,
  IUnitShiftForm,
  IUnitType,
  IUser,
  IVehicle,
  UnitShiftStatus,
  UnitStatus,
} from '@/models';

import { http } from './http';
import {
  ICustomPaginateResponse,
  ICustomUnitStatusDefinition,
  IGetParams,
  IListResponse,
  IPaginatedListResponse,
  IUnitStatusOverview,
} from './types';

export interface AvailableResources {
  vehicles?: IVehicle[];
  users?: IUser[];
  equipments?: IEquipment[];
  polygons?: IPolygonLayer[];
  lines?: ILineLayer[];
  points?: IPointLayer[];
}

export interface CheckResourceResponse {
  users: Record<string, string>;
  vehicles: Record<string, string>;
  equipments: Record<string, string>;
}

export const UnitApi = {
  //Unit type apis
  getUnitType: (id: string): Promise<AxiosResponse<IUnitType>> => {
    return http.get(`api/${ApiVersion.V1}/units/types/${id}`);
  },
  getUnitTypes: (): Promise<AxiosResponse<IUnitType[]>> => {
    return http.get(`api/${ApiVersion.V1}/units/types/`);
  },

  // Unit apis
  getUnits: (
    params?: IGetParams,
  ): Promise<AxiosResponse<IListResponse<IUnit>>> => {
    return http.get(`api/${ApiVersion.V1}/units/units`, { params });
  },
  getPaginatedUnits: (
    params?: IGetParams,
  ): Promise<AxiosResponse<IPaginatedListResponse<IUnit>>> => {
    return http.get(`api/${ApiVersion.V1}/units/units/list`, { params });
  },
  getUnit: (id: string): Promise<AxiosResponse<IUnit>> => {
    return http.get(`api/${ApiVersion.V1}/units/units/${id}`);
  },
  createUnit: (
    data: IUnitForm,
    bypassAssignmentCheck = false,
  ): Promise<AxiosResponse<IUnit>> => {
    return http.post(`api/${ApiVersion.V1}/units/units`, data, {
      params: {
        bypassAssignmentCheck: bypassAssignmentCheck ? 'true' : 'false',
      },
    });
  },

  updateUnit: async (
    { _id, ...data }: IUnitForm,
    bypassAssignmentCheck = false,
  ): Promise<AxiosResponse<IUnit>> => {
    return http.put(`api/${ApiVersion.V1}/units/units/${_id}`, data, {
      params: {
        bypassAssignmentCheck: bypassAssignmentCheck ? 'true' : 'false',
      },
    });
  },
  updateUnitStatus: async (
    id: string,
    status: UnitStatus,
  ): Promise<AxiosResponse<IUnit>> => {
    return http.patch(`api/${ApiVersion.V1}/units/units/${id}/status`, {
      status,
    });
  },
  setUnitOffDuty: async (id: string): Promise<AxiosResponse<IUnit>> => {
    return http.patch(`api/${ApiVersion.V1}/units/units/${id}/off-duty`);
  },
  deleteUnit: (id: string): Promise<AxiosResponse<IUnit>> => {
    return http.delete(`api/${ApiVersion.V1}/units/units/${id}`);
  },
  undeleteUnit: (id: string): Promise<AxiosResponse<IUnit>> => {
    return http.patch(`api/${ApiVersion.V1}/units/units/${id}/restore`);
  },
  getUnitShiftsHistory: (
    params?: IGetParams,
  ): Promise<AxiosResponse<ICustomPaginateResponse<IUnitShift>>> => {
    return http.get(`api/${ApiVersion.V1}/units/shifts/history`, { params });
  },
  // Unit shifts apis
  getUnitShifts: (
    params?: IGetParams,
  ): Promise<AxiosResponse<IListResponse<IUnitShift>>> => {
    return http.get(`api/${ApiVersion.V1}/units/shifts`, { params });
  },
  getUnitShiftsList: (
    params?: IGetParams,
  ): Promise<AxiosResponse<ICustomPaginateResponse<IUnitShiftAssignment>>> => {
    return http.get(`api/${ApiVersion.V1}/units/shifts/list`, { params });
  },
  getUnitShift: (id: string): Promise<AxiosResponse<IUnitShift>> => {
    return http.get(`api/${ApiVersion.V1}/units/shifts/${id}`);
  },
  createUnitShift: (
    data: IUnitShiftForm,
  ): Promise<AxiosResponse<IUnitShift>> => {
    return http.post(`api/${ApiVersion.V1}/units/shifts`, data);
  },
  updateUnitShifts: async (
    _id: string,
    data: Partial<IUnitShiftForm>,
  ): Promise<AxiosResponse<IUnit>> => {
    return http.put(`api/${ApiVersion.V1}/units/shifts/${_id}`, data);
  },
  updateUnitShiftStatus: async (
    _id: string,
    status: UnitShiftStatus,
  ): Promise<AxiosResponse<IUnit>> => {
    return http.patch(`api/${ApiVersion.V1}/units/shifts/${_id}`, { status });
  },
  cancelScheduledUnitShift: async (
    _id: string,
  ): Promise<AxiosResponse<IUnitShift>> => {
    return http.patch(
      `api/${ApiVersion.V1}/units/shifts/${_id}/cancel-scheduled`,
    );
  },

  // Unit filters
  getUnitFilters: (): Promise<AxiosResponse<IUnitFilter[]>> => {
    return http.get(`api/${ApiVersion.V1}/units/filters`);
  },
  createUnitFilter: (
    data: IUnitFilterForm,
  ): Promise<AxiosResponse<IUnitFilter>> => {
    return http.post(`api/${ApiVersion.V1}/units/filters`, data);
  },
  updateUnitFilter: ({
    _id,
    ...rest
  }: IUnitFilterForm): Promise<AxiosResponse<IUnitFilter>> => {
    return http.put(`api/${ApiVersion.V1}/units/filters/${_id}`, rest);
  },
  deleteUnitFilter: (id: string): Promise<AxiosResponse<IUnitFilter>> => {
    return http.delete(`api/${ApiVersion.V1}/units/filters/${id}`);
  },
  getUnitShiftLogs: (params?: IGetParams): Promise<AxiosResponse<any>> => {
    return http.get(`api/${ApiVersion.V1}/cfs/logs/unit-shift`, {
      params: {
        ...params,
      },
    });
  },
  getAllUnitShiftLogs: (params?: IGetParams): Promise<AxiosResponse<any>> => {
    return http.get(`api/${ApiVersion.V1}/cfs/logs/all-unit-shift`, {
      params: {
        ...params,
      },
    });
  },

  // Unit resources apis
  getAvailableResources: (
    type?: string,
    unitId?: string,
    isForReassignment?: boolean,
  ): Promise<AxiosResponse<AvailableResources>> => {
    return http.get(`api/${ApiVersion.V1}/units/resources/available`, {
      params: { type, unitId, isForReassignment },
    });
  },
  checkReallocationResource: (
    unitId: string,
    users?: string[],
    vehicles?: string[],
    equipments?: string[],
  ): Promise<AxiosResponse<CheckResourceResponse>> => {
    return http.post(`api/${ApiVersion.V1}/units/resources/reallocate-check`, {
      unitId,
      users,
      vehicles,
      equipments,
    });
  },
  reallocateResources: (
    unitId: string,
    users?: string[],
    vehicles?: string[],
    equipments?: string[],
  ): Promise<AxiosResponse<any>> => {
    return http.post(`api/${ApiVersion.V1}/units/resources/reallocate`, {
      unitId,
      users,
      vehicles,
      equipments,
    });
  },

  // --- Unit Status Feature API Methods ---

  getUnitStatusOverview: (
    params?: IGetParams,
  ): Promise<AxiosResponse<IListResponse<IUnitStatusOverview>>> => {
    return http.get(`api/${ApiVersion.V1}/units/units/status-overview`, {
      params,
    });
  },

  getAvailableCustomStatuses: (
    params?: IGetParams,
  ): Promise<AxiosResponse<ICustomUnitStatusDefinition[]>> => {
    return http.get(`api/${ApiVersion.V1}/custom-unit-statuses`, {
      params,
    });
  },

  setUnitShiftCustomStatus: (
    shiftId: string,
    payload: { customStatusId: string | null },
  ): Promise<AxiosResponse<IUnitShift>> => {
    return http.patch(
      `api/${ApiVersion.V1}/units/shifts/${shiftId}/custom-status`,
      payload,
    );
  },

  // --- End Unit Status Feature API Methods ---
};
